// slots.ts - Фінансово-операційний трекер слотів та цілі 40 000 грн

interface SlotDayReport {
    slot_date: string;
    pum_hours: number;
    school_hours: number;
    delivery_hours: number;
    delivery_earnings: number;
    sleep_maintained: number;
    notes: string;
}

interface SlotsSummary {
    today: SlotDayReport;
    week: {
        monday: string;
        sunday: string;
        delivery_hours: number;
        delivery_quota: number;
        delivery_debt: number;
        delivery_earnings: number;
        avg_rate: number;
        target_rate: number;
        is_quota_met: boolean;
    };
    month: {
        month_name: string;
        target_income: number;
        school_base: number;
        pum_base: number;
        delivery_earnings: number;
        delivery_hours: number;
        total_income: number;
        remaining: number;
        progress_pct: number;
    };
    settings: {
        target_income: number;
        school_base: number;
        pum_base: number;
        delivery_quota_weekly: number;
        delivery_rate_target: number;
    };
    history: SlotDayReport[];
}

let slotsData: SlotsSummary | null = null;
let slotsInterval: any = null;

const SCHEDULE = [
    { start: '00:00', end: '09:00', name: 'Залізне вікно сну (недоторканне)', type: 'sleep', icon: 'ri-moon-clear-line', color: '#8b5cf6' },
    { start: '09:00', end: '10:30', name: 'Підйом, ранок, сніданок, побут', type: 'free', icon: 'ri-sun-line', color: '#3b82f6' },
    { start: '10:30', end: '11:00', name: 'Дорога до ПУМ', type: 'commute', icon: 'ri-walk-line', color: '#64748b' },
    { start: '11:00', end: '12:30', name: 'ПУМ [Слот Робота-1]', type: 'work', icon: 'ri-macbook-line', color: '#0ea5e9' },
    { start: '12:30', end: '13:00', name: 'Обід', type: 'break', icon: 'ri-restaurant-line', color: '#10b981' },
    { start: '13:00', end: '13:30', name: 'Дорога до ліцею', type: 'commute', icon: 'ri-walk-line', color: '#64748b' },
    { start: '13:30', end: '18:00', name: 'Ліцей (інклюзія) [Слот Робота-2]', type: 'work', icon: 'ri-book-read-line', color: '#f59e0b' },
    { start: '18:00', end: '18:30', name: 'Перехід у точку старту замовлень', type: 'commute', icon: 'ri-e-bike-2-line', color: '#64748b' },
    { start: '18:30', end: '22:30', name: 'Доставка (Glovo + Bolt) [Слот Доставка]', type: 'delivery', icon: 'ri-riding-line', color: '#ec4899' },
    { start: '22:30', end: '24:00', name: 'Повернення, вечеря, душ, особистий час', type: 'free', icon: 'ri-home-4-line', color: '#3b82f6' },
];

function getCurrentSlotInfo() {
    const now = new Date();
    const day = now.getDay(); // 0 = Sun, 6 = Sat
    const isWeekend = (day === 0 || day === 6);

    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const currentTimeStr = `${hours}:${minutes}`;
    const currentMins = now.getHours() * 60 + now.getMinutes();

    if (isWeekend) {
        return {
            isWeekend: true,
            name: 'Вихідний день (Буфер відновлення / Доставки)',
            type: 'weekend',
            icon: 'ri-cup-line',
            color: '#10b981',
            desc: 'За графіком повний вихідний. Якщо є недобір 20 год доставки — рекомендовані піки: 13:00–17:00 або 18:00–22:00.',
            timeUntilNext: ''
        };
    }

    for (let slot of SCHEDULE) {
        const [sh, sm] = slot.start.split(':').map(Number);
        const [eh, em] = slot.end === '24:00' ? [24, 0] : slot.end.split(':').map(Number);
        const startMins = sh * 60 + sm;
        const endMins = eh * 60 + em;

        if (currentMins >= startMins && currentMins < endMins) {
            const diffMins = endMins - currentMins;
            const diffH = Math.floor(diffMins / 60);
            const diffM = diffMins % 60;
            const timeStr = diffH > 0 ? `${diffH} год ${diffM} хв` : `${diffM} хв`;

            return {
                isWeekend: false,
                name: slot.name,
                type: slot.type,
                icon: slot.icon,
                color: slot.color,
                start: slot.start,
                end: slot.end,
                desc: `Триває з ${slot.start} до ${slot.end}`,
                timeUntilNext: `Залишилося: ${timeStr}`
            };
        }
    }

    return {
        isWeekend: false,
        name: 'Графік дня',
        type: 'free',
        icon: 'ri-time-line',
        color: '#64748b',
        desc: 'Поточний слот',
        timeUntilNext: ''
    };
}

async function loadSlotsSummary() {
    try {
        const res = await fetch('/api/slots.php?action=summary');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.success) {
            slotsData = data;
            renderSlotsUI();
        }
    } catch (e) {
        console.error('Failed to load slots summary:', e);
    }
}

function renderSlotsUI() {
    if (!slotsData) return;

    // 1. Live Slot Banner
    const current = getCurrentSlotInfo();
    const liveIconEl = document.getElementById('slots-live-icon');
    const liveTitleEl = document.getElementById('slots-live-title');
    const liveDescEl = document.getElementById('slots-live-desc');
    const liveCountdownEl = document.getElementById('slots-live-countdown');

    if (liveIconEl) {
        liveIconEl.className = `${current.icon} slots-live-icon`;
        liveIconEl.style.color = current.color;
    }
    if (liveTitleEl) liveTitleEl.textContent = current.name;
    if (liveDescEl) liveDescEl.textContent = current.desc;
    if (liveCountdownEl) liveCountdownEl.textContent = current.timeUntilNext;

    // 2. Week Delivery Progress
    const week = slotsData.week;
    const weekHoursEl = document.getElementById('slots-week-hours');
    const weekQuotaEl = document.getElementById('slots-week-quota');
    const weekProgressFillEl = document.getElementById('slots-week-progress-fill');
    const weekStatusBadgeEl = document.getElementById('slots-week-status-badge');
    const weekEarningsEl = document.getElementById('slots-week-earnings');
    const weekRateEl = document.getElementById('slots-week-rate');

    if (weekHoursEl) weekHoursEl.textContent = `${week.delivery_hours.toFixed(1)} год`;
    if (weekQuotaEl) weekQuotaEl.textContent = `/ ${week.delivery_quota} год`;
    
    const weekPct = Math.min(100, Math.round((week.delivery_hours / week.delivery_quota) * 100));
    if (weekProgressFillEl) weekProgressFillEl.style.width = `${weekPct}%`;

    if (weekStatusBadgeEl) {
        if (week.is_quota_met) {
            weekStatusBadgeEl.className = 'slots-badge badge-success';
            weekStatusBadgeEl.innerHTML = '<i class="ri-checkbox-circle-line"></i> Квоту 20/20 закрито!';
        } else if (week.delivery_debt > 0) {
            weekStatusBadgeEl.className = 'slots-badge badge-warning';
            weekStatusBadgeEl.innerHTML = `<i class="ri-alarm-warning-line"></i> Борг на буфер: ${week.delivery_debt.toFixed(1)} год`;
        } else {
            weekStatusBadgeEl.className = 'slots-badge badge-info';
            weekStatusBadgeEl.innerHTML = '<i class="ri-time-line"></i> В процесі виконання';
        }
    }

    if (weekEarningsEl) weekEarningsEl.textContent = `${Math.round(week.delivery_earnings).toLocaleString('uk-UA')} грн`;
    if (weekRateEl) {
        const rate = Math.round(week.avg_rate);
        weekRateEl.textContent = rate > 0 ? `${rate} грн/год` : `ціль ~${week.target_rate} грн/год`;
        if (rate >= week.target_rate) {
            weekRateEl.style.color = 'var(--color-success)';
        } else if (rate > 0) {
            weekRateEl.style.color = 'var(--color-warning)';
        }
    }

    // 3. Month Financial Progress (Target: 40 000 грн)
    const month = slotsData.month;
    const monthTotalEl = document.getElementById('slots-month-total');
    const monthTargetEl = document.getElementById('slots-month-target');
    const monthFillEl = document.getElementById('slots-month-fill');
    const monthSchoolEl = document.getElementById('slots-month-school');
    const monthPumEl = document.getElementById('slots-month-pum');
    const monthDeliveryEl = document.getElementById('slots-month-delivery');
    const monthRemainingEl = document.getElementById('slots-month-remaining');

    if (monthTotalEl) monthTotalEl.textContent = `${Math.round(month.total_income).toLocaleString('uk-UA')} грн`;
    if (monthTargetEl) monthTargetEl.textContent = `/ ${Math.round(month.target_income).toLocaleString('uk-UA')} грн (${month.progress_pct}%)`;
    if (monthFillEl) monthFillEl.style.width = `${month.progress_pct}%`;

    if (monthSchoolEl) monthSchoolEl.textContent = `${Math.round(month.school_base).toLocaleString('uk-UA')} грн`;
    if (monthPumEl) monthPumEl.textContent = `${Math.round(month.pum_base).toLocaleString('uk-UA')} грн`;
    if (monthDeliveryEl) monthDeliveryEl.textContent = `${Math.round(month.delivery_earnings).toLocaleString('uk-UA')} грн`;
    if (monthRemainingEl) {
        if (month.remaining <= 0) {
            monthRemainingEl.innerHTML = '<span style="color:var(--color-success)">План виконано! 🎉</span>';
        } else {
            monthRemainingEl.textContent = `Залишилося: ${Math.round(month.remaining).toLocaleString('uk-UA')} грн`;
        }
    }

    // 4. Fill Today Report Inputs (if not already modified by user)
    const today = slotsData.today;
    const dateInput = document.getElementById('slot-input-date') as HTMLInputElement | null;
    const pumInput = document.getElementById('slot-input-pum') as HTMLInputElement | null;
    const schoolInput = document.getElementById('slot-input-school') as HTMLInputElement | null;
    const deliveryHoursInput = document.getElementById('slot-input-delivery-hours') as HTMLInputElement | null;
    const deliveryEarningsInput = document.getElementById('slot-input-delivery-earnings') as HTMLInputElement | null;
    const sleepCheck = document.getElementById('slot-input-sleep') as HTMLInputElement | null;
    const notesInput = document.getElementById('slot-input-notes') as HTMLInputElement | null;

    if (dateInput && !dateInput.value) dateInput.value = today.slot_date;
    if (pumInput && !pumInput.value) pumInput.value = String(today.pum_hours || 1.5);
    if (schoolInput && !schoolInput.value) schoolInput.value = String(today.school_hours || 4.5);
    if (deliveryHoursInput && !deliveryHoursInput.value) deliveryHoursInput.value = String(today.delivery_hours || 4.0);
    if (deliveryEarningsInput && !deliveryEarningsInput.value) deliveryEarningsInput.value = String(today.delivery_earnings || '');
    if (sleepCheck) sleepCheck.checked = today.sleep_maintained !== 0;
    if (notesInput && !notesInput.value) notesInput.value = today.notes || '';

    // 5. History Table
    renderHistoryTable(slotsData.history);
}

function renderHistoryTable(history: SlotDayReport[]) {
    const tbody = document.getElementById('slots-history-tbody');
    if (!tbody) return;

    if (!history || history.length === 0) {
        tbody.innerHTML = '<tr><td colspan="7" style="text-align:center;color:var(--text-muted);padding:20px;">Ще немає збережених записів</td></tr>';
        return;
    }

    tbody.innerHTML = history.map(row => {
        const rate = Number(row.delivery_hours) > 0 ? Math.round(Number(row.delivery_earnings) / Number(row.delivery_hours)) : 0;
        const sleepBadge = Number(row.sleep_maintained) === 1
            ? '<span class="slots-pill pill-green"><i class="ri-moon-line"></i> Дотримано</span>'
            : '<span class="slots-pill pill-red"><i class="ri-error-warning-line"></i> Зрив</span>';
        
        return `
            <tr>
                <td style="font-weight:600;color:var(--text-title);">${row.slot_date}</td>
                <td>${Number(row.pum_hours).toFixed(1)} год</td>
                <td>${Number(row.school_hours).toFixed(1)} год</td>
                <td><strong style="color:var(--brand-orange);">${Number(row.delivery_hours).toFixed(1)} год</strong></td>
                <td>${Math.round(Number(row.delivery_earnings)).toLocaleString('uk-UA')} грн</td>
                <td>${rate > 0 ? `${rate} грн/год` : '—'}</td>
                <td>${sleepBadge}</td>
            </tr>
        `;
    }).join('');
}

// Global functions for HTML interaction
(window as any).saveSlotsDailyReport = async function() {
    const dateInput = document.getElementById('slot-input-date') as HTMLInputElement | null;
    const pumInput = document.getElementById('slot-input-pum') as HTMLInputElement | null;
    const schoolInput = document.getElementById('slot-input-school') as HTMLInputElement | null;
    const deliveryHoursInput = document.getElementById('slot-input-delivery-hours') as HTMLInputElement | null;
    const deliveryEarningsInput = document.getElementById('slot-input-delivery-earnings') as HTMLInputElement | null;
    const sleepCheck = document.getElementById('slot-input-sleep') as HTMLInputElement | null;
    const notesInput = document.getElementById('slot-input-notes') as HTMLInputElement | null;

    const payload = {
        slot_date: dateInput ? dateInput.value : new Date().toISOString().split('T')[0],
        pum_hours: pumInput ? parseFloat(pumInput.value) || 0 : 1.5,
        school_hours: schoolInput ? parseFloat(schoolInput.value) || 0 : 4.5,
        delivery_hours: deliveryHoursInput ? parseFloat(deliveryHoursInput.value) || 0 : 0,
        delivery_earnings: deliveryEarningsInput ? parseFloat(deliveryEarningsInput.value) || 0 : 0,
        sleep_maintained: sleepCheck && sleepCheck.checked ? 1 : 0,
        notes: notesInput ? notesInput.value.trim() : ''
    };

    try {
        const res = await fetch('/api/slots.php?action=save_day', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (data.success) {
            if ((window as any).showMessage) {
                (window as any).showMessage(data.message || 'Звіт дня збережено!', 'success');
            } else {
                alert('Звіт успішно збережено!');
            }
            await loadSlotsSummary();
        } else {
            throw new Error(data.error || 'Помилка збереження');
        }
    } catch (e: any) {
        console.error(e);
        if ((window as any).showMessage) {
            (window as any).showMessage(e.message || 'Не вдалося зберегти звіт', 'error');
        } else {
            alert('Помилка: ' + e.message);
        }
    }
};

(window as any).refreshSlotsData = loadSlotsSummary;

// Initialize on DOMContentLoaded and tab clicks
document.addEventListener('DOMContentLoaded', () => {
    loadSlotsSummary();

    // Auto-refresh live timeline every 30 seconds
    if (slotsInterval) clearInterval(slotsInterval);
    slotsInterval = setInterval(() => {
        if (slotsData) renderSlotsUI();
    }, 30000);

    // If slots tab is clicked, reload fresh data
    const slotsBtn = document.querySelector('[data-tab="slots"]');
    if (slotsBtn) {
        slotsBtn.addEventListener('click', () => {
            loadSlotsSummary();
        });
    }
});

export { loadSlotsSummary };
